/**
 * ContentAccessService - Service centralisé pour la gestion d'accès au contenu
 * Inspiré de l'architecture de la PR #1
 *
 * Fonctionnalités:
 * - Vérification d'accès centralisée
 * - Gestion des modales d'authentification
 * - Support des différents types de contenu
 * - Intégration avec l'authentification anonyme
 */

import {Routes} from '@src/Constant/routes';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, filter} from 'rxjs/operators';

export type ContentType = 'audio' | 'video' | 'article' | 'quiz';

export interface ContentAccessData {
  contentType: ContentType;
  contentId: string | number;
  redirectTo?: Routes;
  accessType?: string;
  paywall?: any;
  metadata?: {
    title?: string;
    isLive?: boolean;
    quizType?: 'personality' | 'knowledge' | 'survey';
  };
}

export interface ContentAccessState {
  // États d'accès
  hasAccess: boolean;
  canPreview: boolean;
  isLoading: boolean;

  // États des modales
  showAuthModal: boolean;
  showPaywall: boolean;

  // Contexte
  currentContentData?: ContentAccessData;
  isAnonymous: boolean;
  isLoggedIn: boolean;

  // Preview timer
  previewTimeRemaining: number;
  isPreviewActive: boolean;
}

export interface ContentAccessContext {
  isPlayer?: boolean;
  forceCheck?: boolean;
}

class ContentAccessService {
  // État principal du service
  private _state = new BehaviorSubject<ContentAccessState>({
    hasAccess: false,
    canPreview: false,
    isLoading: false,
    showAuthModal: false,
    showPaywall: false,
    isAnonymous: false,
    isLoggedIn: false,
    previewTimeRemaining: 0,
    isPreviewActive: false,
  });

  // Observables publics
  public readonly state: Observable<ContentAccessState> =
    this._state.asObservable();
  public readonly authModal$ = this._state.pipe(
    filter(
      state => state.showAuthModal !== this.getCurrentState().showAuthModal,
    ),
    distinctUntilChanged(
      (prev, curr) => prev.showAuthModal === curr.showAuthModal,
    ),
  );

  public readonly paywall$ = this._state.pipe(
    filter(state => state.showPaywall !== this.getCurrentState().showPaywall),
    distinctUntilChanged((prev, curr) => prev.showPaywall === curr.showPaywall),
  );

  // Méthodes d'injection pour les fonctions métier
  private paywallMethods: {
    canSeeContent?: (accessType?: string) => boolean;
    canPreview?: (paywall?: any) => boolean;
  } = {};

  private anonymousAuthMethod?: () => Promise<boolean>;

  constructor() {}

  // Méthodes publiques
  public getCurrentState(): ContentAccessState {
    return this._state.value;
  }

  public setPaywallMethods(methods: {
    canSeeContent?: (accessType?: string) => boolean;
    canPreview?: (paywall?: any) => boolean;
  }) {
    this.paywallMethods = methods;
  }

  public setAnonymousAuthMethod(method: () => Promise<boolean>) {
    this.anonymousAuthMethod = method;
  }

  public updateAuthState(isLoggedIn: boolean, isAnonymous: boolean) {
    const currentState = this.getCurrentState();
    if (
      currentState.isLoggedIn !== isLoggedIn ||
      currentState.isAnonymous !== isAnonymous
    ) {
      this._state.next({
        ...currentState,
        isLoggedIn,
        isAnonymous,
      });
    }
  }

  // Méthode principale de vérification d'accès
  public async checkContentAccess(
    data: ContentAccessData,
    context: ContentAccessContext = {},
  ): Promise<void> {
    const currentState = this.getCurrentState();

    if (
      !context.forceCheck &&
      currentState.currentContentData &&
      this.isSameContent(currentState.currentContentData, data)
    ) {
      return;
    }

    // Mise à jour de l'état de loading
    this._state.next({
      ...currentState,
      isLoading: true,
      currentContentData: data,
    });

    try {
      const accessDecision = await this.determineAccess(data, context);
      this.applyAccessDecision(accessDecision, data);
    } catch (error) {
      this.applyAccessDecision(
        {
          hasAccess: false,
          canPreview: false,
          showAuthModal: false,
          showPaywall: false,
          reason: 'Error occurred',
        },
        data,
      );
    }
  }

  // Actions publiques
  public readonly actions = {
    checkContentAccess: this.checkContentAccess.bind(this),
    closeAuthModal: () => {
      const currentState = this.getCurrentState();
      this._state.next({
        ...currentState,
        showAuthModal: false,
      });
    },
    closePaywall: () => {
      const currentState = this.getCurrentState();
      this._state.next({
        ...currentState,
        showPaywall: false,
      });
    },
    reset: () => {
      this._state.next({
        hasAccess: false,
        canPreview: false,
        isLoading: false,
        showAuthModal: false,
        showPaywall: false,
        isAnonymous: false,
        isLoggedIn: false,
        previewTimeRemaining: 0,
        isPreviewActive: false,
      });
    },
  };

  // Méthodes privées
  private isSameContent(
    content1: ContentAccessData,
    content2: ContentAccessData,
  ): boolean {
    return (
      content1.contentType === content2.contentType &&
      content1.contentId === content2.contentId &&
      content1.accessType === content2.accessType
    );
  }

  private async determineAccess(
    data: ContentAccessData,
    context: ContentAccessContext,
  ): Promise<AccessDecision> {
    const currentState = this.getCurrentState();

    // Si l'utilisateur n'est pas connecté
    if (!currentState.isLoggedIn) {
      return await this.handleUnauthenticatedAccess(data, context);
    }

    // Si l'utilisateur est connecté
    return await this.handleAuthenticatedAccess(data, context);
  }

  private async handleUnauthenticatedAccess(
    data: ContentAccessData,
    context: ContentAccessContext,
  ): Promise<AccessDecision> {
    const currentState = this.getCurrentState();

    if (currentState.isAnonymous) {
      return {
        hasAccess: false,
        canPreview: false,
        showAuthModal: true,
        showPaywall: false,
        reason: 'Authentication required for anonymous user',
      };
    }

    if (
      data.contentType === 'quiz' &&
      data.metadata?.quizType === 'personality'
    ) {
      return {
        hasAccess: false,
        canPreview: false,
        showAuthModal: true,
        showPaywall: false,
        reason: 'Personality quiz requires registration',
      };
    }

    if (this.anonymousAuthMethod) {
      try {
        const hasAnonymousAccess = await this.anonymousAuthMethod();
        if (hasAnonymousAccess) {
          return {
            hasAccess: true,
            canPreview: false,
            showAuthModal: false,
            showPaywall: false,
            reason: 'Anonymous access granted',
          };
        }
      } catch {
        // best-effort
      }
    }

    return {
      hasAccess: false,
      canPreview: false,
      showAuthModal: true,
      showPaywall: false,
      reason: 'Authentication required',
    };
  }

  private async handleAuthenticatedAccess(
    data: ContentAccessData,
    context: ContentAccessContext,
  ): Promise<AccessDecision> {
    // Vérifier l'accès via les méthodes paywall
    if (this.paywallMethods.canSeeContent && data.accessType) {
      const hasPaywallAccess = this.paywallMethods.canSeeContent(
        data.accessType,
      );
      if (hasPaywallAccess) {
        return {
          hasAccess: true,
          canPreview: false,
          showAuthModal: false,
          showPaywall: false,
          reason: 'Full access granted',
        };
      }
    }

    // Vérifier si un aperçu est possible
    if (this.paywallMethods.canPreview && data.paywall) {
      const canPreview = this.paywallMethods.canPreview(data.paywall);
      if (canPreview) {
        return {
          hasAccess: false,
          canPreview: true,
          showAuthModal: false,
          showPaywall: false,
          reason: 'Preview available',
        };
      }
    }

    // Par défaut : paywall
    return {
      hasAccess: false,
      canPreview: false,
      showAuthModal: false,
      showPaywall: true,
      reason: 'Premium subscription required',
    };
  }

  private applyAccessDecision(
    decision: AccessDecision,
    data: ContentAccessData,
  ) {
    const currentState = this.getCurrentState();

    this._state.next({
      ...currentState,
      hasAccess: decision.hasAccess,
      canPreview: decision.canPreview,
      showAuthModal: decision.showAuthModal,
      showPaywall: decision.showPaywall,
      isLoading: false,
      currentContentData: data,
    });
  }
}

interface AccessDecision {
  hasAccess: boolean;
  canPreview: boolean;
  showAuthModal: boolean;
  showPaywall: boolean;
  reason: string;
}

// Instance singleton
export const contentAccessService = new ContentAccessService();

export default contentAccessService;
